
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Star, TrendingUp, ExternalLink, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

interface FeaturedStartupProps {
  name: string;
  description: string;
  category: string;
  founderName: string;
  website?: string;
  image?: string;
  featured?: boolean;
}

const FeaturedStartupCard: React.FC<FeaturedStartupProps> = ({
  name,
  description,
  category,
  founderName,
  website,
  image,
  featured = false
}) => {
  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.02,
      y: -5,
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    }
  };

  const badgeVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        delay: 0.3,
        duration: 0.4
      }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        delay: 0.2,
        duration: 0.4
      }
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      className="relative"
    >
      <Card className={`overflow-hidden border-2 transition-all duration-300 ${
        featured 
          ? 'border-[#FF8C00] shadow-xl shadow-[#FF8C00]/20 bg-gradient-to-br from-[#FF8C00]/5 to-orange-500/5' 
          : 'border-border hover:border-[#FF8C00]/50'
      }`}>
        {featured && (
          <motion.div 
            className="absolute -top-2 -right-2 z-10"
            variants={badgeVariants}
            initial="hidden"
            animate="visible"
          >
            <Badge className="bg-[#FF8C00] text-white px-3 py-1 shadow-lg">
              <Sparkles className="h-3 w-3 mr-1" />
              Featured
            </Badge>
          </motion.div>
        )}

        {image && (
          <motion.div 
            className="h-48 relative overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <motion.img 
              src={image} 
              alt={name} 
              className="w-full h-full object-cover"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </motion.div>
        )}

        <CardHeader className="pb-3">
          <motion.div variants={contentVariants}>
            <CardTitle className="text-xl flex items-center gap-2">
              {name}
              {featured && <Star className="h-4 w-4 text-[#FF8C00] fill-current" />}
            </CardTitle>
            <Badge variant="outline" className="w-fit mt-2">
              {category}
            </Badge>
          </motion.div>
        </CardHeader>

        <CardContent className="pt-0">
          <motion.div variants={contentVariants}>
            <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
              {description}
            </p>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Founded by</p>
                <p className="font-medium text-sm">{founderName}</p>
              </div>
              
              {website && (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => window.open(website, '_blank')}
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Visit
                  </Button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default FeaturedStartupCard;
